import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["buttonRef", "use", "_useNativeButton", "children"];
import styled from 'foundations-theming/utils';
import { useState, useCallback } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UITabs from 'UIComponents/nav/UITabs';
import UITab from 'UIComponents/nav/UITab';
import UITextInput from 'UIComponents/input/UITextInput';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIButton from 'UIComponents/button/UIButton';
// @ts-expect-error untyped import
import UIInputIconWrapper from 'UIComponents/input/internal/UIInputIconWrapper';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { getNumDaysForMonth } from 'automation-ui-enrollment/constants/scheduledEnrollment/DayOfMonthValues';
import { MonthTranslationKeys, MonthValues } from 'automation-ui-enrollment/constants/scheduledEnrollment/MonthValues';
import { getRecurringDateMomentFormat } from 'automation-ui-enrollment/components/scheduledEnrollment/utils/recurringDateFormat';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MonthGrid = styled.div.withConfig({
  displayName: "RecurringDateInput__MonthGrid",
  componentId: "sc-3oeayz-0"
})(["display:grid;grid-template-columns:repeat(3,1fr);gap:8px;"]);
const DateGrid = styled.div.withConfig({
  displayName: "RecurringDateInput__DateGrid",
  componentId: "sc-3oeayz-1"
})(["display:grid;grid-template-columns:repeat(7,1fr);gap:4px;"]);
const StyledButton = styled(UIButton).withConfig({
  displayName: "RecurringDateInput__StyledButton",
  componentId: "sc-3oeayz-2"
})(["margin:0px !important;padding:12px !important;&:hover{background-color:", "!important;}&.selected{background-color:", "!important;color:", "!important;}"], ({
  theme
}) => theme.color['do-not-use-calypso-light'], ({
  theme
}) => theme.color['do-not-use-calypso'], ({
  theme
}) => theme.color['do-not-use-olaf']);
export default function RecurringDateInput({
  value: {
    month: selectedMonth,
    dayOfMonth: selectedDayOfMonth
  },
  onChange,
  onFocus,
  onBlur,
  readOnly,
  use
}) {
  const format = getRecurringDateMomentFormat();
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState('month');
  const MonthDayInput = useCallback(_ref => {
    let {
        buttonRef
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return /*#__PURE__*/_jsx(UIInputIconWrapper, {
      iconName: "date",
      size: "default",
      children: /*#__PURE__*/_jsx(UITextInput, Object.assign({}, rest, {
        use: use,
        readOnly: readOnly,
        onFocus: onFocus,
        inputRef: buttonRef,
        placeholder: getRecurringDateMomentFormat({
          placeholder: true
        }),
        defaultValue: I18n.moment({
          year: 0,
          // Allows selection of 2/29 on non-leap years.
          month: selectedMonth - 1,
          date: selectedDayOfMonth
        }).format(format),
        onBlur: ({
          target: {
            value
          }
        }) => {
          const moment = I18n.moment(value, format);
          const month = moment.month() + 1;
          const dayOfMonth = moment.date();
          if (moment.isValid()) {
            if (
            // Sending a duplicate change event can mess with the dropdown
            month !== selectedMonth && dayOfMonth !== selectedDayOfMonth) {
              onChange(syntheticEvent({
                month,
                dayOfMonth
              }));
            }
          } else {
            onChange(syntheticEvent(null));
          }
          onBlur === null || onBlur === void 0 || onBlur();
        }
      }))
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [onChange, format, selectedMonth, selectedDayOfMonth, readOnly, use]);
  const MonthDayContent = useCallback(() => {
    return /*#__PURE__*/_jsxs(UITabs, {
      panelClassName: "m-all-0",
      fill: true,
      defaultSelected: tab,
      onSelectedChange: ({
        target: {
          value
        }
      }) => setTab(value),
      children: [/*#__PURE__*/_jsx(UITab, {
        tabId: "month",
        title: "Month",
        children: /*#__PURE__*/_jsx(MonthGrid, {
          children: Object.values(MonthValues).map(month => /*#__PURE__*/_jsx(StyledButton, {
            use: "primary-white",
            className: 'text-left' + (month === selectedMonth ? " selected" : ""),
            onClick: () => {
              const daysInNewMonth = getNumDaysForMonth(month);
              const lastDayOfNewMonth = daysInNewMonth[daysInNewMonth.length - 1];
              onChange(syntheticEvent({
                month,
                // If the next selected month has less days than the current selected date,
                // find the last day in the month and use that one as the fallback date.
                dayOfMonth: Math.min(selectedDayOfMonth, lastDayOfNewMonth)
              }));
              setTab('day');
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: MonthTranslationKeys[month]
            })
          }, month))
        })
      }), /*#__PURE__*/_jsx(UITab, {
        tabId: "day",
        title: "Day",
        children: /*#__PURE__*/_jsx(DateGrid, {
          children: getNumDaysForMonth(selectedMonth).map(dayOfMonth => /*#__PURE__*/_jsx(StyledButton, {
            use: "primary-white",
            className: dayOfMonth === selectedDayOfMonth ? "selected" : "",
            onClick: () => {
              onChange(syntheticEvent({
                month: selectedMonth,
                dayOfMonth
              }));
              setIsOpen(false);
              setTab('month');
            },
            children: dayOfMonth
          }, dayOfMonth))
        })
      })]
    });
  }, [onChange, tab, selectedMonth, selectedDayOfMonth]);
  return /*#__PURE__*/_jsx(UIDropdown, {
    use: "unstyled",
    closeOnMenuClick: false,
    open: isOpen,
    onOpenChange: ({
      target: {
        value
      }
    }) => setIsOpen(value),
    Button: MonthDayInput,
    Content: MonthDayContent,
    dropdownClassName: "p-all-0",
    readOnly: readOnly
  });
}